/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
  WritableSignerAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const INITIALIZE_REWARD_V2_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([91, 1, 77, 50, 235, 229, 133, 49]);

export function getInitializeRewardV2DiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_REWARD_V2_DISCRIMINATOR,
  );
}

export type InitializeRewardV2Instruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountRewardAuthority extends string | AccountMeta = string,
  TAccountFunder extends string | AccountMeta = string,
  TAccountWhirlpool extends string | AccountMeta = string,
  TAccountRewardMint extends string | AccountMeta = string,
  TAccountRewardTokenBadge extends string | AccountMeta = string,
  TAccountRewardVault extends string | AccountMeta = string,
  TAccountRewardTokenProgram extends
    | string
    | AccountMeta = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountSystemProgram extends
    | string
    | AccountMeta = "11111111111111111111111111111111",
  TAccountRent extends
    | string
    | AccountMeta = "SysvarRent111111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountRewardAuthority extends string
        ? ReadonlySignerAccount<TAccountRewardAuthority> &
            AccountSignerMeta<TAccountRewardAuthority>
        : TAccountRewardAuthority,
      TAccountFunder extends string
        ? WritableSignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountWhirlpool extends string
        ? WritableAccount<TAccountWhirlpool>
        : TAccountWhirlpool,
      TAccountRewardMint extends string
        ? ReadonlyAccount<TAccountRewardMint>
        : TAccountRewardMint,
      TAccountRewardTokenBadge extends string
        ? ReadonlyAccount<TAccountRewardTokenBadge>
        : TAccountRewardTokenBadge,
      TAccountRewardVault extends string
        ? WritableSignerAccount<TAccountRewardVault> &
            AccountSignerMeta<TAccountRewardVault>
        : TAccountRewardVault,
      TAccountRewardTokenProgram extends string
        ? ReadonlyAccount<TAccountRewardTokenProgram>
        : TAccountRewardTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountRent extends string
        ? ReadonlyAccount<TAccountRent>
        : TAccountRent,
      ...TRemainingAccounts,
    ]
  >;

export interface InitializeRewardV2InstructionData {
  discriminator: ReadonlyUint8Array;
  rewardIndex: number;
}

export interface InitializeRewardV2InstructionDataArgs {
  rewardIndex: number;
}

export function getInitializeRewardV2InstructionDataEncoder(): FixedSizeEncoder<InitializeRewardV2InstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["rewardIndex", getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_REWARD_V2_DISCRIMINATOR,
    }),
  );
}

export function getInitializeRewardV2InstructionDataDecoder(): FixedSizeDecoder<InitializeRewardV2InstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["rewardIndex", getU8Decoder()],
  ]);
}

export function getInitializeRewardV2InstructionDataCodec(): FixedSizeCodec<
  InitializeRewardV2InstructionDataArgs,
  InitializeRewardV2InstructionData
> {
  return combineCodec(
    getInitializeRewardV2InstructionDataEncoder(),
    getInitializeRewardV2InstructionDataDecoder(),
  );
}

export interface InitializeRewardV2Input<
  TAccountRewardAuthority extends string = string,
  TAccountFunder extends string = string,
  TAccountWhirlpool extends string = string,
  TAccountRewardMint extends string = string,
  TAccountRewardTokenBadge extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountRent extends string = string,
> {
  rewardAuthority: TransactionSigner<TAccountRewardAuthority>;
  funder: TransactionSigner<TAccountFunder>;
  whirlpool: Address<TAccountWhirlpool>;
  rewardMint: Address<TAccountRewardMint>;
  rewardTokenBadge: Address<TAccountRewardTokenBadge>;
  rewardVault: TransactionSigner<TAccountRewardVault>;
  rewardTokenProgram?: Address<TAccountRewardTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  rent?: Address<TAccountRent>;
  rewardIndex: InitializeRewardV2InstructionDataArgs["rewardIndex"];
}

export function getInitializeRewardV2Instruction<
  TAccountRewardAuthority extends string,
  TAccountFunder extends string,
  TAccountWhirlpool extends string,
  TAccountRewardMint extends string,
  TAccountRewardTokenBadge extends string,
  TAccountRewardVault extends string,
  TAccountRewardTokenProgram extends string,
  TAccountSystemProgram extends string,
  TAccountRent extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: InitializeRewardV2Input<
    TAccountRewardAuthority,
    TAccountFunder,
    TAccountWhirlpool,
    TAccountRewardMint,
    TAccountRewardTokenBadge,
    TAccountRewardVault,
    TAccountRewardTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeRewardV2Instruction<
  TProgramAddress,
  TAccountRewardAuthority,
  TAccountFunder,
  TAccountWhirlpool,
  TAccountRewardMint,
  TAccountRewardTokenBadge,
  TAccountRewardVault,
  TAccountRewardTokenProgram,
  TAccountSystemProgram,
  TAccountRent
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    rewardAuthority: {
      value: input.rewardAuthority ?? null,
      isWritable: false,
    },
    funder: { value: input.funder ?? null, isWritable: true },
    whirlpool: { value: input.whirlpool ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    rewardTokenBadge: {
      value: input.rewardTokenBadge ?? null,
      isWritable: false,
    },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardTokenProgram: {
      value: input.rewardTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    rent: { value: input.rent ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.rewardTokenProgram.value) {
    accounts.rewardTokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.rent.value) {
    accounts.rent.value =
      "SysvarRent111111111111111111111111111111111" as Address<"SysvarRent111111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("rewardAuthority", accounts.rewardAuthority),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("whirlpool", accounts.whirlpool),
      getAccountMeta("rewardMint", accounts.rewardMint),
      getAccountMeta("rewardTokenBadge", accounts.rewardTokenBadge),
      getAccountMeta("rewardVault", accounts.rewardVault),
      getAccountMeta("rewardTokenProgram", accounts.rewardTokenProgram),
      getAccountMeta("systemProgram", accounts.systemProgram),
      getAccountMeta("rent", accounts.rent),
    ],
    data: getInitializeRewardV2InstructionDataEncoder().encode(
      args as InitializeRewardV2InstructionDataArgs,
    ),
    programAddress,
  } as InitializeRewardV2Instruction<
    TProgramAddress,
    TAccountRewardAuthority,
    TAccountFunder,
    TAccountWhirlpool,
    TAccountRewardMint,
    TAccountRewardTokenBadge,
    TAccountRewardVault,
    TAccountRewardTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >);
}

export interface ParsedInitializeRewardV2Instruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    rewardAuthority: TAccountMetas[0];
    funder: TAccountMetas[1];
    whirlpool: TAccountMetas[2];
    rewardMint: TAccountMetas[3];
    rewardTokenBadge: TAccountMetas[4];
    rewardVault: TAccountMetas[5];
    rewardTokenProgram: TAccountMetas[6];
    systemProgram: TAccountMetas[7];
    rent: TAccountMetas[8];
  };
  data: InitializeRewardV2InstructionData;
}

export function parseInitializeRewardV2Instruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeRewardV2Instruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 9) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 9,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      rewardAuthority: getNextAccount(),
      funder: getNextAccount(),
      whirlpool: getNextAccount(),
      rewardMint: getNextAccount(),
      rewardTokenBadge: getNextAccount(),
      rewardVault: getNextAccount(),
      rewardTokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
      rent: getNextAccount(),
    },
    data: getInitializeRewardV2InstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
